import { useState, useEffect } from 'react';
import { DATE_TIME_CONFIG } from '../config/dateTimeConfig';

interface CountdownResult {
  hours: number;
  minutes: number;
  seconds: number;
  formatted: string;
}

/**
 * Hook that calculates and updates countdown to the next game reset time.
 * Updates every second.
 *
 * @returns CountdownResult with hours, minutes, seconds and formatted string
 */
export const useResetCountdown = (): CountdownResult => {
  const [countdown, setCountdown] = useState<CountdownResult>(() => calculateCountdown());

  useEffect(() => {
    // Update immediately
    setCountdown(calculateCountdown());

    // Update every second
    const intervalId = setInterval(() => {
      setCountdown(calculateCountdown());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return countdown;
};

/**
 * Calculate time remaining until next reset (2:00 AM UTC)
 */
function calculateCountdown(): CountdownResult {
  const now = new Date();
  const resetHour = DATE_TIME_CONFIG.GAME_RESET.HOUR;
  const resetMinute = DATE_TIME_CONFIG.GAME_RESET.MINUTE;

  // Create target time for today at reset time (UTC)
  const target = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    resetHour,
    resetMinute,
    0,
    0
  ));

  // If we've passed today's reset time, target tomorrow
  if (now.getTime() >= target.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }

  const diffMs = target.getTime() - now.getTime();
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return { hours, minutes, seconds, formatted };
}

export default useResetCountdown;
